import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, of, BehaviorSubject, Subscription, combineLatest} from 'rxjs';
import { map,  switchMap } from 'rxjs/operators';

import { PageCriteriaModelInterface } from '../../Misc/models/page-criteria.model.interface';
import { TargetingSearch } from '../../../Discover/Targeting/models/targeting-search.model';
import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { TargetingResultModel } from '../../../Discover/Targeting/models/targeting-result.model';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { FilterTypeModel } from '../../Filter/models/abstract-filter-type.model';
import { TargetingSearchResponseApiModel } from '../../../api/targeting/model/targeting-search-response.api.model';
import { JournodexSubjectsService, SubjectUtils } from '../../Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../../Discover/Core/services/journodex-enums.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { BaseSearchService, TargetingFacetCollection } from '../../../Discover/Targeting/services/base-search.service';
import { EntityIdApiModel } from '../../../api/targeting/model/entity-id-api-model';
import { TargetingList } from '../../../Discover/Targeting/models/targeting-list.model';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { OutletContactTabType, TargetingRhpContextsService } from '../../TargetingShared/services/targeting-rhp-contexts.service';
import { SortFieldEnum } from '../../TargetingShared/enum/SortField.enum';
import { JournodexEntityFactory } from '../../../Discover/Targeting/factories/journodex-entity.factory';
import { TargetingResultFactory } from '../../../Discover/Targeting/factories/targeting-result.factory';
import { EmploymentFactory } from '../../../Discover/Targeting/factories/employment.factory';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { DiscoverRoutingService } from '../../../Discover/DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteTypeEnum } from '../../../Discover/DiscoverRouter/enums/discover-route-type.enum';
import { UserAppTargetingDisplaySettingsModel } from '../../../Core/Users/models/user-app-default-display-preferences.model';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DistributionApi } from '@api/distributions/distribution.api';

@Injectable()
export class StaffSearchService extends BaseSearchService {

    private primaryContact: TargetingResultModel = null;
    private primaryContact$: BehaviorSubject<TargetingResultModel> = new BehaviorSubject<TargetingResultModel>(this.primaryContact);

    contactSortFields: LocalizedEnum[] = [
        {
            enumValue: SortFieldEnum.FIRST_NAME,
            displayString: 'Discover.targeting.sortFields.firstName'
        },
        {
            enumValue: SortFieldEnum.LAST_NAME,
            displayString: 'Discover.targeting.sortFields.lastName'
        },
        {
            enumValue: SortFieldEnum.JOB_TITLE,
            displayString: 'Discover.targeting.sortFields.jobTitle'
        },
        {
            enumValue: SortFieldEnum.OUTLET_NAME,
            displayString: 'Discover.targeting.sortFields.outletName'
        },
        {
            enumValue: SortFieldEnum.COUNTRY_NAME,
            displayString: 'Common.country'
        },
        {
            enumValue: SortFieldEnum.PROVINCE_NAME,
            displayString: 'Common.province'
        },
        {
            enumValue: SortFieldEnum.CITY_NAME,
            displayString: 'Common.city'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            displayString: 'Discover.targeting.sortFields.uniqueVisitors'
        }
    ];

    private readonly targetingListRoutes = [
        DiscoverRouteTypeEnum.targetingListHype,
        DiscoverRouteTypeEnum.targetingListSearchResult,
        DiscoverRouteTypeEnum.privateContacts
    ];
    private readonly savedSearchesRoutes = [
        DiscoverRouteTypeEnum.targetingSearchHype,
        DiscoverRouteTypeEnum.targetingSearch,
        DiscoverRouteTypeEnum.targetingSearchUpdate,
        DiscoverRouteTypeEnum.targetingResults,
        DiscoverRouteTypeEnum.targetingSavedSearch,
        DiscoverRouteTypeEnum.targetingSavedSearchResults,
    ];

    protected primaryContactRequest: Subscription;

    outletId: string;
    staffPanelVisible: boolean = false;

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingRhpContextsService: TargetingRhpContextsService,
        protected outletsApi: OutletsPublicApi,
        private journodexEntityFactory: JournodexEntityFactory,
        private employmentFactory: EmploymentFactory,
        protected targetingResultFactory: TargetingResultFactory,
        protected discoverRoutingService: DiscoverRoutingService,
        protected userPreferencesService: UserPreferencesService,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionApiService: DistributionApi
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApiService
        );
        this.setFilters(this.allFilters);

        this.targetingSortFields$.next(this.contactSortFields);
        this.sortField = this.contactSortFields[0];
        this.sortField$.next(this.contactSortFields[0]);
        this.setPageCriteria({page: 1, pageSize: 25}, false);

        this.sortOrder = SortOrderEnum.ascending;
        this.sortOrder$.next(this.sortOrder);

        this.subscriptions.push(
            this.targetingRhpContextsService.getOutletId().subscribe(outletId => {
                if (outletId && this.outletId !== outletId) {
                    this.outletId = outletId;
                    if (this.staffPanelVisible) {
                        this.clearFilters();
                        this.doSearch(true, true);
                        this.fetchPrimaryContact();
                    }
                }
            }),
            this.targetingRhpContextsService.getCurrentOutletContactTab().subscribe(tab => {
                this.setStaffPanelVisible(tab === OutletContactTabType.staffSearch);
            }),
            combineLatest([
                this.discoverRoutingService.getCurrentRoute(),
                this.userPreferencesService.getUserAppPreferences()
            ]).subscribe(([route, userAppPrefs]) => {
                if (this.targetingListRoutes.includes(route.type)) {
                    this.pageCriteria.pageSize = userAppPrefs.targetingPrefs.targetedListsPrefs.defaultDisplaySettings.pageSize;
                } else if (this.savedSearchesRoutes.includes(route.type)) {
                    this.pageCriteria.pageSize = userAppPrefs.targetingPrefs.savedSearchesPrefs.defaultDisplaySettings.pageSize;
                }
                this.setPageCriteria(this.pageCriteria, false);
            })
        );
    }

    getFilterDefinitions(): FilterTypeModel[] {
        const filters: FilterTypeModel[] = [
            this.audienceReachFilter,
            this.subjectFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.languageFilter,
            this.locationFilter,
            this.contactNameFilter,
            this.statusFilter,
            this.targetAudienceRegionFilter
        ];
        // TODO: add aoc and list filter, alphabetically. Backend not implemented at the moment

        return filters;
    }

    setStaffPanelVisible(visible: boolean) {
        if (this.staffPanelVisible !== visible) {
            this.staffPanelVisible = visible;
            if (visible && this.outletId) {
                this.doSearch(true, true);
                this.fetchPrimaryContact();
            }
        }
    }

    getPrimaryContact(): Observable<TargetingResultModel> {
        return this.primaryContact$;
    }

    fetchPrimaryContact() {
        this.primaryContactRequest = this.outletsApi.getPrimaryContact(this.outletId).subscribe({
            next: async (contact) => {
                if (contact) {
                    // the staff card works with a search result model, so let's imitate it
                    this.primaryContact = this.targetingResultFactory.createEmptyTargetingResult();
                    this.primaryContact.entityType = EntityType.CONTACT;
                    this.primaryContact.contactModel = await this.journodexEntityFactory.createContact(contact);
                    this.primaryContact.id = contact.id;
                    this.primaryContact.firstName = contact.firstName;
                    this.primaryContact.lastName = contact.lastName;
                    this.primaryContact.avatarUrl = contact.avatarUrl;
                    this.primaryContact.selectedEmployment = await this.employmentFactory.create(
                        contact.employments.find(x => x.outletInfo?.id === this.outletId)
                    );
                    this.primaryContact.subjectChips = SubjectUtils.convertSubjectsToChips(this.primaryContact.contactModel.subjects);
                    this.primaryContactRequest = null;
                    this.fetchListMemberships();
                }
            },
            error: (err: HttpErrorResponse) => {
                if (err.status === 404) {
                    // no prob; the outlet has no contacts
                    this.primaryContact = null;
                    this.primaryContactRequest = null;
                    this.fetchListMemberships();
                } else {
                    console.log(err);
                }
            }
        });
    }

    /**
     * Updates the page criteria
     */
    setPageCriteria(pageCriteria: PageCriteriaModelInterface, search: boolean = true) {
        this.page = pageCriteria.page;
        if (this.page <= this.totalNumberOfPages) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            if (search) {
                this.doSearch();
            }
        }
    }

    /**
     * Checks that the page criteria can be updated
     * @NOTE This is an observable so that users can make the decision via dialogs etc.
     */
    canSetPageCriteria(): Observable<boolean> {
        return of(true);
    }

    public doSearch(refreshPrimaryFacets: boolean = false, refreshFilteredFacets: boolean = false): Observable<TargetingSearchResponseApiModel> {

        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        const search = new TargetingSearch();
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        const mainSearchRequest: Observable<TargetingSearchResponseApiModel> = this.fastSearch(search, filters);

        // just to make sure it runs, in case the caller doesn't subscribe
        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.subscribe({
            next: () => {
                this.mainSearchRequest = null;
                this.fetchListMemberships();
                // this.setSearchState(SearchStatesEnum.success);
            },
            error: (err) => {
                console.log(err);
                // this.setSearchState(SearchStatesEnum.error, err);
            }
        });

        let filteredFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshFilteredFacets) {
            if (this.filteredFacetRequest) {
                this.filteredFacetRequest.unsubscribe();
            }
            filteredFacetRequest = this.facetSearch(search, filters);

            this.filteredFacetRequest = filteredFacetRequest.subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        let primaryFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshPrimaryFacets) {
            if (this.primaryFacetRequest) {
                this.primaryFacetRequest.unsubscribe();
            }
            if (isFiltered || !filteredFacetRequest) {
                primaryFacetRequest = this.facetSearch(search);
            } else {
                primaryFacetRequest = filteredFacetRequest;
            }
            this.primaryFacetRequest = primaryFacetRequest.subscribe(collection => {
                this.setPrimaryFacets(collection);
                this.primaryFacetRequest = null;
            });
        }

        return mainSearchRequest;
    }

    // Fast search is just the results without any facets
    fastSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<TargetingSearchResponseApiModel> {
        const rows = this.pageCriteria.pageSize;
        const start = (this.pageCriteria.page - 1) * rows;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        const searchRequest: Observable<TargetingSearchResponseApiModel> = this.targetingSearchesApiService.performStaffSearch(
            this.outletId,
            search.toApiModel(false, filters).query,
            start,
            rows,
            sortOrder,
            sortField
        );

        return searchRequest.pipe(
            switchMap(async response => {
                await this.setSearchResults(response);
                return response;
            })
        );
    }

    facetSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<TargetingFacetCollection> {
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        const searchRequest: Observable<TargetingSearchResponseApiModel> = this.targetingSearchesApiService.performStaffSearch(
            this.outletId,
            search.toApiModel(true, filters).query,
            0,
            0,
            sortOrder,
            sortField
        );


        return searchRequest.pipe(map(response => {
            return this.parseFacets(response.facetResults);
        }));
    }

    // We only emit the results and primary contact once this completes
    // It slows the load a little bit but simplifies the code
    fetchListMemberships() {
        // Both of these have to be done so we can get the memberships
        // for the primary contact as well as the search results at the same time
        if (this.mainSearchRequest || this.primaryContactRequest) {
            return;
        }

        let contactsToFetch: EntityIdApiModel[] = [];
        if (this.primaryContact) {
            contactsToFetch = this.results
                .filter(x => x.id !== this.primaryContact.id)
                .map(x => {
                    return <EntityIdApiModel> {
                        contactUuid : x.id
                    };
                });
            contactsToFetch.push(<EntityIdApiModel> {
                contactUuid : this.primaryContact.id
            });
        } else {
            contactsToFetch = this.results
                .map(x => {
                    return <EntityIdApiModel> {
                        contactUuid : x.id
                    };
                });
        }

        this.targetingListsApiService.getListMemberships(contactsToFetch).subscribe(listMemberships => {
            for (const result of this.results) {
                const membership = listMemberships.find(memberships => memberships.uuid === result.id);
                result.listMemberships = membership?.targetingLists?.map(x => new TargetingList(x)) || [];
            }
            if (this.primaryContact) {
                const primaryMembership = listMemberships.find(memberships => memberships.uuid === this.primaryContact.id);
                this.primaryContact.listMemberships = primaryMembership?.targetingLists?.map(x => new TargetingList(x)) || [];
            }

            this.nResults$.next(this.nResults);
            this.results$.next(this.results);
            this.searchLoading$.next(false);
            this.primaryContact$.next(this.primaryContact);
        });
    }
}
